import { FormikProvider, useFormik } from 'formik';
import Head from 'next/head';
import Link from 'next/link';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useResponsive } from '@hooks/useResponsive';
import LoginLayout from '@layouts/v2/login.layout';
import ButtonV2, {
  ButtonV2Rounded,
  ButtonV2Size,
  ButtonV2Style,
} from '@ui-kit/v2/Button';
import { InputTextDecorator } from '@ui-kit/v2/inputs/InputText';
import classNames from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Script from 'next/script';
import ReactGA from 'react-ga4';
import {
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineXCircle,
} from 'react-icons/hi';
import config from '../config';
import { GOOGLE_ANALYTICS_EVENTS } from '../constants/googleAnalyticsEvent';
import WEB_PATHS from '../constants/webPaths';
import useTranslation from '../i18n/useTranslation';
import PasswordRulesV2 from './PasswordRulesV2';
import PdpaSignup from './PdpaSignup';
import SignupSuccess from './SignupSuccess';
import TermAndCon from './TermAndCon';
import signupSchema from './signup.schema';
import { useSignup } from './useSignup';

export interface ISignup {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  leadformurl: string;
  phoneNumber: string;
}

const SignUpForm = (props) => {
  const { t, router, error, validateSignupForm } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { lg } = useResponsive();
  const emailRef = useRef(null);
  const formik = useFormik<ISignup>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      leadformurl: router.pathname,
    },
    validateOnBlur: false,
    validationSchema: signupSchema,
    onSubmit: validateSignupForm,
  });

  const focusInput = (ref: React.MutableRefObject<HTMLInputElement>) => {
    ref.current.focus();
  };

  useEffect(() => {
    focusInput(emailRef);
  }, []);

  return (
    <FormikProvider value={formik}>
      <form
        className="flex min-h-0 flex-auto flex-col gap-y-8 overflow-y-auto"
        onSubmit={formik.handleSubmit}
        autoComplete="off"
      >
        <div className="flex flex-col gap-y-4">
          <span className="text-left text-subheading font-bold lg:text-heading">
            {t('signupPage.metaTitle')}
          </span>
          {InputTextDecorator.decorate(
            {
              inputProps: {
                placeholder: t('signinPage.email'),
                onBlur: formik.handleBlur,
                onChange: formik.handleChange,
                className:
                  formik.touched.email && formik.errors.email
                    ? 'text-redv2-900'
                    : '',
                type: 'email',
                ref: emailRef,
              },
              valid: !(formik.touched.email && formik.errors.email),
            },
            [
              InputTextDecorator.withFormikField('email'),
              InputTextDecorator.withErrorMessage(
                <div className="text-left text-footnote text-red-200">
                  <p>{formik.errors.email && t(formik.errors.email)}</p>
                </div>,
              ),
              InputTextDecorator.withLabelOnTop(
                <p className="text-left text-p2 font-regular text-grayv2-900">
                  {t('signinPage.email')}
                </p>,
              ),
            ],
          )}
          {InputTextDecorator.decorate(
            {
              rightComponent: (
                <div>
                  <HiOutlineEye
                    className={classNames(
                      `h-5 w-5 cursor-pointer text-gray-600 hover:text-grayv2-900 ${
                        showPassword ? 'hidden' : ''
                      }`,
                    )}
                    onClick={() => setShowPassword(true)}
                  />
                  <HiOutlineEyeOff
                    className={classNames(
                      `h-5 w-5 cursor-pointer text-gray-600 hover:text-grayv2-900 ${
                        showPassword ? '' : 'hidden'
                      }`,
                    )}
                    onClick={() => setShowPassword(false)}
                  />
                </div>
              ),
              inputProps: {
                placeholder: t('password'),
                onBlur: formik.handleBlur,
                onChange: formik.handleChange,
                className:
                  formik.touched.password && formik.errors.password
                    ? 'text-redv2-900'
                    : '',
                type: showPassword ? 'text' : 'password',
              },
              valid: !(formik.touched.password && formik.errors.password),
            },
            [
              InputTextDecorator.withFormikField('password'),
              InputTextDecorator.withErrorMessage(
                <div className="text-left text-footnote text-red-200">
                  <p>
                    {formik.errors.password === 'required'
                      ? t('required')
                      : t('signinPage.invalidPasswordFormat')}
                  </p>
                </div>,
              ),
              InputTextDecorator.withLabelOnTop(
                <p className="text-left text-p2 font-regular text-grayv2-900">
                  {t('password')}
                </p>,
              ),
            ],
          )}
          {formik.touched.password && formik.errors.password && (
            <div className="flex flex-col gap-y-4">
              <PasswordRulesV2
                touched={formik.touched.password}
                password={formik.values.password}
              />
            </div>
          )}
          {InputTextDecorator.decorate(
            {
              rightComponent: (
                <div>
                  <HiOutlineEye
                    className={classNames(
                      `h-5 w-5 cursor-pointer text-gray-600 hover:text-grayv2-900 ${
                        showConfirmPassword ? 'hidden' : ''
                      }`,
                    )}
                    onClick={() => setShowConfirmPassword(true)}
                  />
                  <HiOutlineEyeOff
                    className={classNames(
                      `h-5 w-5 cursor-pointer text-gray-600 hover:text-grayv2-900 ${
                        showConfirmPassword ? '' : 'hidden'
                      }`,
                    )}
                    onClick={() => setShowConfirmPassword(false)}
                  />
                </div>
              ),
              inputProps: {
                placeholder: t('signupPage.confirmPassword'),
                onBlur: formik.handleBlur,
                onChange: formik.handleChange,
                className:
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                    ? 'text-redv2-900'
                    : '',
                type: showConfirmPassword ? 'text' : 'password',
              },
              valid: !(
                formik.touched.confirmPassword && formik.errors.confirmPassword
              ),
            },
            [
              InputTextDecorator.withFormikField('confirmPassword'),
              InputTextDecorator.withErrorMessage(
                <div className="text-left text-footnote text-red-200">
                  <p>
                    {formik.errors.confirmPassword &&
                      t(formik.errors.confirmPassword)}
                  </p>
                </div>,
              ),
              InputTextDecorator.withLabelOnTop(
                <p className="text-left text-p2 font-regular text-grayv2-900">
                  {t('signupPage.confirmPassword')}
                </p>,
              ),
            ],
          )}
        </div>
        <div className="flex flex-col gap-y-4">
          <span className="text-left text-subheading font-bold lg:text-heading">
            {t('signupPage.personal')}
          </span>
          {InputTextDecorator.decorate(
            {
              inputProps: {
                placeholder: t('firstName'),
                onBlur: formik.handleBlur,
                onChange: formik.handleChange,
                className:
                  formik.touched.firstName && formik.errors.firstName
                    ? 'text-redv2-900'
                    : '',
                type: 'text',
              },
              valid: !(formik.touched.firstName && formik.errors.firstName),
            },
            [
              InputTextDecorator.withFormikField('firstName'),
              InputTextDecorator.withErrorMessage(
                <div className="text-left text-footnote text-red-200">
                  <p>{formik.errors.firstName && t(formik.errors.firstName)}</p>
                </div>,
              ),
              InputTextDecorator.withLabelOnTop(
                <p className="text-left text-p2 font-regular text-grayv2-900">
                  {t('firstName')}
                </p>,
              ),
            ],
          )}
          {InputTextDecorator.decorate(
            {
              inputProps: {
                placeholder: t('lastName'),
                onBlur: formik.handleBlur,
                onChange: formik.handleChange,
                className:
                  formik.touched.lastName && formik.errors.lastName
                    ? 'text-redv2-900'
                    : '',
                type: 'text',
              },
              valid: !(formik.touched.lastName && formik.errors.lastName),
            },
            [
              InputTextDecorator.withFormikField('lastName'),
              InputTextDecorator.withErrorMessage(
                <div className="text-left text-footnote text-red-200">
                  <p>{formik.errors.lastName && t(formik.errors.lastName)}</p>
                </div>,
              ),
              InputTextDecorator.withLabelOnTop(
                <p className="text-left text-p2 font-regular text-grayv2-900">
                  {t('lastName')}
                </p>,
              ),
            ],
          )}
          {InputTextDecorator.decorate(
            {
              inputProps: {
                placeholder: t('signupPage.phoneNumber'),
                onBlur: formik.handleBlur,
                onChange: formik.handleChange,
                className:
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                    ? 'text-redv2-900'
                    : '',
                type: 'text',
              },
              valid: !(formik.touched.phoneNumber && formik.errors.phoneNumber),
            },
            [
              InputTextDecorator.withFormikField('phoneNumber'),
              InputTextDecorator.withErrorMessage(
                <div className="text-left text-footnote text-red-200">
                  <p>
                    {formik.errors.phoneNumber && t(formik.errors.phoneNumber)}
                  </p>
                </div>,
              ),
              InputTextDecorator.withLabelOnTop(
                <p className="text-left text-p2 font-regular text-grayv2-900">
                  {t('signupPage.phoneNumber')}
                </p>,
              ),
            ],
          )}
        </div>
        {error && (
          <div className="flex rounded-md bg-redv2-50 px-2 py-4 text-left text-p1 font-semibold text-redv2-700">
            <span className=" mr-2 text-xl">
              <HiOutlineXCircle />
            </span>
            {error}
          </div>
        )}
        <div className="flex flex-col-reverse justify-between gap-y-4 lg:flex-row">
          <div className="text-center text-p1 font-regular text-grayv2-900 lg:text-left">
            <span>{t('signupPage.haveAccount')}</span>
            <span className="ml-2 whitespace-nowrap text-p2 underline">
              <Link href={{ pathname: WEB_PATHS.LOGIN, query: router.query }}>
                {t('signinPage.signIn')}
              </Link>
            </span>
          </div>
          <ButtonV2
            variantClassName={classNames(
              ButtonV2Rounded.large,
              ButtonV2Style.primary,
              lg ? ButtonV2Size.small : ButtonV2Size.medium,
              'w-full lg:w-1/3 h-fit',
            )}
            buttonProps={{
              disabled: !formik.dirty || !formik.isValid,
              type: 'submit',
              onClick: () =>
                ReactGA.event({
                  category: GOOGLE_ANALYTICS_EVENTS.EVENT_CATEGORY.BUTTON,
                  action: GOOGLE_ANALYTICS_EVENTS.EVENT_ACTION.BUTTON.CLICK,
                  label: GOOGLE_ANALYTICS_EVENTS.getLabel({
                    item: GOOGLE_ANALYTICS_EVENTS.LABEL_CONSTANTS.SIGN_UP_PAGE
                      .SIGN_UP,
                    buttonName: 'Sign up',
                  }),
                }),
            }}
          >
            {t('next')}
          </ButtonV2>
        </div>
      </form>
    </FormikProvider>
  );
};

function Signup(props) {
  const { validateSignupForm, error, router } = props;
  const { t } = useTranslation();

  const head = useMemo(
    () => (
      <Head>
        <title>
          {t('headerText')} | {t('signupPage.metaTitle')}
        </title>
      </Head>
    ),
    [],
  );

  return (
    <LoginLayout head={head} contentScrollable>
      <div className="flex min-h-0 flex-auto flex-col rounded-3xl bg-white p-8">
        <Script
          src={`https://www.google.com/recaptcha/api.js?render=${config.RECAPTCHA_SITE_KEY}`}
        ></Script>
        <div className="mb-[30px] flex justify-center">
          <div className="p-3.5">
            <div className="relative h-[54px] w-[54px]">
              <Image
                src="/assets/yournextu-logo-vertical-compact-v2.png"
                layout="fill"
                objectFit="contain"
              />
            </div>
          </div>
        </div>

        <SignUpForm
          router={router}
          t={t}
          error={error}
          validateSignupForm={validateSignupForm}
        />
      </div>
    </LoginLayout>
  );
}

export default function SignupPage() {
  const {
    step,
    validateSignupForm,
    error,
    handleSubmitTermandCon,
    submitSignupForm,
  } = useSignup();
  const router = useRouter();

  return (
    <>
      {step === 0 && (
        <Signup
          validateSignupForm={validateSignupForm}
          error={error}
          router={router}
        />
      )}
      {step === 1 && <TermAndCon handleSubmit={handleSubmitTermandCon} />}
      {step === 2 && (
        <PdpaSignup submitSignupForm={submitSignupForm} error={error} />
      )}
      {step === 3 && <SignupSuccess router={router} />}
    </>
  );
}
